import type {
  Device,
  Subscription,
  Invoice,
  Operator,
  TrainingJob,
  Organisation,
  UnassignedDevice,
  OTAJob,
  DeviceOTAStatus,
  TelemetryMetric,
  TelemetryQueryRequest,
  TelemetryQueryResponse,
  TelemetryFaultsRequest,
  TelemetryFaultsResponse,
} from '../types';
import type { DashboardDoc, DashboardRecord, DashboardSummary } from '../dashboards/types';
import type { SkuDoc, SkuRecord, SkuSummary, UploadUrlResponse, DownloadUrlResponse } from '../skus/types';

// ── Configuration ───────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

// ── Telemetry / dashboards binding ─────────────────────────────────
// The deployed VITE_API_URL points directly at API Gateway, which has NO
// telemetry routes; only CloudFront routes /api/telemetry/* (incl. the
// dashboards CRUD) to the Fastify server. These namespaces therefore ALWAYS
// use the relative /api base. Local dev: vite.config.ts proxies /api/telemetry
// to a local server (cd server && npm run dev).
const TELEMETRY_BASE = '/api';

type GetToken = () => Promise<string>;
let _getToken: GetToken = async () => '';

/** Called once by App.tsx to wire up the auth token provider */
export function setTokenProvider(fn: GetToken) {
  _getToken = fn;
}

// ── HTTP helpers ────────────────────────────────────────────────────

async function headers(): Promise<HeadersInit> {
  const token = await _getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Parse a 2xx body as JSON, but fail clearly when it isn't. CloudFront rewrites an
// origin 403/404 to the SPA index.html (200) distribution-wide, so a denied API call
// can arrive as HTML; surface an actionable message instead of a raw
// "JSON.parse: unexpected character" from the caller.
async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, 'Session may have expired or you lack permission — sign in again.');
  }
}

async function get<T>(path: string, base: string = API_BASE): Promise<T> {
  const res = await fetch(`${base}${path}`, { headers: await headers() });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return parseJson<T>(res);
}

async function post<T>(path: string, body?: unknown, base: string = API_BASE): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: await headers(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return parseJson<T>(res);
}

async function put<T>(path: string, body?: unknown, base: string = API_BASE): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    method: 'PUT',
    headers: await headers(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return parseJson<T>(res);
}

async function del(path: string, base: string = API_BASE): Promise<void> {
  const res = await fetch(`${base}${path}`, {
    method: 'DELETE',
    headers: await headers(),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(ApiError.getMessage(status, body));
    this.name = 'ApiError';
  }

  static getMessage(status: number, body: string): string {
    // Extract error message from body if it's JSON
    let detail = '';
    try {
      const parsed = JSON.parse(body);
      detail = parsed.message || parsed.error || body;
    } catch {
      detail = body || 'Unknown error';
    }

    switch (status) {
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'Conflict: this action cannot be completed due to a conflicting state.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'A server error occurred. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'The service is temporarily unavailable. Please try again shortly.';
      default:
        return detail.length > 100 ? `Error ${status}` : detail;
    }
  }
}

// ── Fleet ───────────────────────────────────────────────────────────

export const fleet = {
  listDevices: () => get<Device[]>('/fleet/devices'),
  getDevice: (serial: string) => get<Device>(`/fleet/devices/${serial}`),
  listUnassigned: () => get<UnassignedDevice[]>('/fleet/unassigned'),
  pushConfig: (serial: string, config: { telemetry_rate_hz: number; reason: string }) =>
    post(`/fleet/devices/${serial}/config`, config),
};

// ── Billing ─────────────────────────────────────────────────────────

export const billing = {
  getSubscription: () => get<Subscription>('/billing/subscription'),
  listInvoices: () => get<Invoice[]>('/billing/invoices'),
  requestUpgrade: (tier: string) => post('/billing/upgrade', { tier }),
};

// ── Users ───────────────────────────────────────────────────────────

export const users = {
  list: () => get<Operator[]>('/users'),
  invite: (email: string, role: string, opts?: { name?: string; org_id?: string; org_type?: string }) =>
    post<Operator>('/users/invite', { email, role, ...opts }),
  updateRole: (sub: string, role: string) => put<Operator>(`/users/${sub}/role`, { role }),
  revoke: (sub: string) => del(`/users/${sub}`),
};

// ── Training / Models ───────────────────────────────────────────────

// Training jobs now live on the Fastify queue (/api/telemetry/training, Phase 3),
// not the legacy /models Lambda — so these use TELEMETRY_BASE.
export const models = {
  listJobs: () => get<{ jobs: TrainingJob[] }>('/telemetry/training', TELEMETRY_BASE).then((r) => r.jobs),
  getJob: (id: string) => get<TrainingJob>(`/telemetry/training/${encodeURIComponent(id)}`, TELEMETRY_BASE),
  create: (data: { sku_id: string; dataset_version: string }) =>
    post<TrainingJob>('/telemetry/training', data, TELEMETRY_BASE),
  cancel: (id: string) =>
    post<TrainingJob>(`/telemetry/training/${encodeURIComponent(id)}/cancel`, undefined, TELEMETRY_BASE),
};

// ── Organisations ───────────────────────────────────────────────────

export const orgs = {
  list: () => get<Organisation[]>('/orgs'),
  get: (id: string) => get<Organisation>(`/orgs/${id}`),
  create: (data: { org_name: string; org_type: string; tier: string; region?: string; parent_org_id?: string }) =>
    post<Organisation>('/orgs', data),
  update: (id: string, data: Partial<Organisation>) =>
    put<Organisation>(`/orgs/${id}`, data),
  delete: (id: string) => del(`/orgs/${id}`),
  commissionDevice: (orgId: string, serial: string) =>
    post(`/orgs/${orgId}/commission`, { serial }),
  decommissionDevice: (orgId: string, serial: string) =>
    del(`/orgs/${orgId}/commission/${serial}`),
};

// ── OTA ─────────────────────────────────────────────────────────────

export const ota = {
  listJobs: () => get<OTAJob[]>('/ota/jobs'),
  listDeviceStatus: () => get<DeviceOTAStatus[]>('/ota/status'),
  cancelJob: (jobId: string) => post<void>(`/ota/jobs/${encodeURIComponent(jobId)}/cancel`),
  cancelDeviceExecution: (serial: string) => post<void>(`/ota/devices/${encodeURIComponent(serial)}/cancel-execution`),
};

// ── Telemetry (Phase 0 server) ──────────────────────────────────────
// Always uses TELEMETRY_BASE (relative /api) — see note near the constant.

export const telemetry = {
  listMetrics: () => get<{ metrics: TelemetryMetric[] }>('/telemetry/metrics', TELEMETRY_BASE),
  query:       (req: TelemetryQueryRequest) => post<TelemetryQueryResponse>('/telemetry/query', req, TELEMETRY_BASE),
  faults:      (req: TelemetryFaultsRequest) => post<TelemetryFaultsResponse>('/telemetry/faults', req, TELEMETRY_BASE),
};

// ── Dashboards (Phase 2) ────────────────────────────────────────────
// CRUD lives behind /api/telemetry/dashboards on the Fastify server, so it
// shares the relative TELEMETRY_BASE binding.

export const dashboards = {
  list: () => get<{ dashboards: DashboardSummary[] }>('/telemetry/dashboards', TELEMETRY_BASE),
  get: (id: string) => get<DashboardRecord>(`/telemetry/dashboards/${encodeURIComponent(id)}`, TELEMETRY_BASE),
  create: (doc: DashboardDoc) => post<DashboardRecord>('/telemetry/dashboards', doc, TELEMETRY_BASE),
  update: (id: string, doc: DashboardDoc, expectedUpdatedAt: string) =>
    put<DashboardRecord>(`/telemetry/dashboards/${encodeURIComponent(id)}`, { ...doc, expected_updated_at: expectedUpdatedAt }, TELEMETRY_BASE),
  remove: (id: string) => del(`/telemetry/dashboards/${encodeURIComponent(id)}`, TELEMETRY_BASE),
};

// ── SKU Library (Phase 1) ───────────────────────────────────────────
// CRUD lives behind /api/telemetry/skus on the Fastify server, so it shares the
// relative TELEMETRY_BASE binding with telemetry/dashboards.

export const skus = {
  list: () => get<{ skus: SkuSummary[] }>('/telemetry/skus', TELEMETRY_BASE),
  get: (id: string) => get<SkuRecord>(`/telemetry/skus/${encodeURIComponent(id)}`, TELEMETRY_BASE),
  create: (doc: SkuDoc) => post<SkuRecord>('/telemetry/skus', doc, TELEMETRY_BASE),
  update: (id: string, doc: SkuDoc, expectedUpdatedAt: string) =>
    put<SkuRecord>(`/telemetry/skus/${encodeURIComponent(id)}`, { ...doc, expected_updated_at: expectedUpdatedAt }, TELEMETRY_BASE),
  remove: (id: string) => del(`/telemetry/skus/${encodeURIComponent(id)}`, TELEMETRY_BASE),

  // ── Artifacts (Phase 2): presign → upload to S3 → attach ──
  cadUploadUrl: (id: string, body: { filename: string; bytes: number }) =>
    post<UploadUrlResponse>(`/telemetry/skus/${encodeURIComponent(id)}/cad-upload-url`, body, TELEMETRY_BASE),
  attachCad: (id: string, body: { filename: string; sha256: string; bytes: number }) =>
    post<SkuRecord>(`/telemetry/skus/${encodeURIComponent(id)}/cad`, body, TELEMETRY_BASE),
  cadDownloadUrl: (id: string) =>
    get<DownloadUrlResponse>(`/telemetry/skus/${encodeURIComponent(id)}/cad-download-url`, TELEMETRY_BASE),
  datasetUploadUrl: (id: string, version: string, body: { filename: string; bytes: number }) =>
    post<UploadUrlResponse>(`/telemetry/skus/${encodeURIComponent(id)}/datasets/${encodeURIComponent(version)}/upload-url`, body, TELEMETRY_BASE),
  attachDataset: (
    id: string,
    body: { version: string; filename: string; sha256: string; bytes: number; image_count: number; class_count: number; annotation_format: string },
  ) => post<SkuRecord>(`/telemetry/skus/${encodeURIComponent(id)}/datasets`, body, TELEMETRY_BASE),
  datasetDownloadUrl: (id: string, version: string) =>
    get<DownloadUrlResponse>(`/telemetry/skus/${encodeURIComponent(id)}/datasets/${encodeURIComponent(version)}/download-url`, TELEMETRY_BASE),

  // ── Model registry (Phase 4) ──
  registerModel: (id: string, sourceJobId: string) =>
    post<SkuRecord>(`/telemetry/skus/${encodeURIComponent(id)}/models`, { source_job_id: sourceJobId }, TELEMETRY_BASE),
  modelBinUploadUrl: (id: string, modelId: string, body: { filename: string; bytes: number }) =>
    post<UploadUrlResponse>(`/telemetry/skus/${encodeURIComponent(id)}/models/${encodeURIComponent(modelId)}/bin-upload-url`, body, TELEMETRY_BASE),
  attachModelBin: (id: string, modelId: string, body: { filename: string; sha256: string; bytes: number }) =>
    post<SkuRecord>(`/telemetry/skus/${encodeURIComponent(id)}/models/${encodeURIComponent(modelId)}/bin`, body, TELEMETRY_BASE),
  activateModel: (id: string, modelId: string) =>
    post<SkuRecord>(`/telemetry/skus/${encodeURIComponent(id)}/models/${encodeURIComponent(modelId)}/activate`, undefined, TELEMETRY_BASE),
  modelDownloadUrl: (id: string, modelId: string, artifact?: 'onnx' | 'bin') =>
    get<DownloadUrlResponse>(`/telemetry/skus/${encodeURIComponent(id)}/models/${encodeURIComponent(modelId)}/download-url${artifact ? `?artifact=${artifact}` : ''}`, TELEMETRY_BASE),
};
